import { Col, Flex, Row, Spin } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Card from 'src/components/card';
import DocumentsList from 'src/components/documentsList';
import {
  APP_ROUTES,
  DOCUMENT_TYPE_KEY,
  MEDIA_QUERIES,
  MEDICAL_HISTORY,
  REACT_APP_HOSPITAL_CODE,
  USER_ACTIVITY_EVENT,
  USER_ACTIVITY_TYPE
} from 'src/enums';
import useWindowDimensions from 'src/hooks/useWindowDimensions';
import {
  documentsSelectors,
  useDispatch,
  userSelectors,
  useSelector
} from 'src/store';
import { useTheme } from 'src/styles/theme';
import { IDocument } from 'src/types/documents.types';
import {
  createLog,
  getErrorMessage,
  notificationError,
  onClickDownloadPdf,
  translateError
} from 'src/utils';

import NoMatch from '../noMatch/NoMatchScreen';
import classes from './style/MedicalHistory.module.css';

interface IMedicalHistoryProps {
  exa?: string;
  children?: any;
  setActive?: any;
  activeKey?: string;
}

const MedicalHistory: React.FunctionComponent<IMedicalHistoryProps> = (
  props
) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useSelector(userSelectors.selectUser);
  const hospital = useSelector(userSelectors.selectUserHospital);
  const { width } = useWindowDimensions();
  const allDocuments = useSelector(documentsSelectors.selectDocumentsData);
  const allDocumentsLoader = useSelector(
    documentsSelectors.selectDocumentsLoader
  );
  const allDocumentsError = useSelector(
    documentsSelectors.selectDocumentsError
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // state
  const [openPdfState, setOpenPdfState] = React.useState<{
    loader: boolean;
    error: null | string;
  }>({ loader: false, error: null });

  const getColumnSpan = (length: number) => {
    switch (length) {
      case 1:
        return 24;
      case 4:
        return 6;
      case 3:
        return 8;
      case 2:
        return 12;
      default:
        return 12;
    }
  };

  const getTop4DocumentsByDate = (documents: IDocument[]) => {
    const sortedDocuments = [...documents].sort(
      (a, b) => new Date(b.docDate).getTime() - new Date(a.docDate).getTime()
    );
    if (width && width >= MEDIA_QUERIES.DESKTOP) {
      return sortedDocuments.slice(0, 4);
    }
    if ((width ?? 0) < MEDIA_QUERIES.MOBILE) {
      return sortedDocuments.slice(0, 4);
    }
    if ((width ?? 0) < MEDIA_QUERIES.TABLET) {
      return sortedDocuments.slice(0, 2);
    }
    if ((width ?? 0) > MEDIA_QUERIES.TABLET) {
      return sortedDocuments.slice(0, 3);
    }

    return sortedDocuments.slice(0, 4);
  };

  const documentTypes = [
    DOCUMENT_TYPE_KEY.ER_SUMMARY,
    DOCUMENT_TYPE_KEY.VISIT_SUMMARY,
    DOCUMENT_TYPE_KEY.RELEASE_DOCS
  ];

  const isOtherListNotEmpty = (excludedType: DOCUMENT_TYPE_KEY) => {
    if (allDocuments) {
      return documentTypes
        .filter((type) => type !== excludedType)
        .some(
          (type) =>
            allDocuments?.ReleaseDocList?.filter(
              (document) => document.caseType === type
            ).length > 0
        );
    }
  };

  const areAllListsNotEmpty = () => {
    if (allDocuments?.ReleaseDocList) {
      return documentTypes.every(
        (type) =>
          allDocuments?.ReleaseDocList.filter(
            (document) => document.caseType === type
          ).length > 0
      );
    }
  };

  const getDocType = (docType: string) => {
    switch (docType) {
      case DOCUMENT_TYPE_KEY.ER_SUMMARY:
        return APP_ROUTES.ER_SUMMARY;
      case DOCUMENT_TYPE_KEY.VISIT_SUMMARY:
        return APP_ROUTES.VISIT_SUMMARY;
      case DOCUMENT_TYPE_KEY.RELEASE_DOCS:
        return APP_ROUTES.RELEASE_DOCS;
      default:
        return '';
    }
  };

  const getDocTypeName = (docType: string) => {
    switch (docType) {
      case DOCUMENT_TYPE_KEY.ER_SUMMARY:
        return t('medicalHistory.erSummary');
      case DOCUMENT_TYPE_KEY.VISIT_SUMMARY:
        return t('medicalHistory.visitSummary');
      case DOCUMENT_TYPE_KEY.RELEASE_DOCS:
        return t('medicalHistory.releaseDoc');
      default:
        return '';
    }
  };

  const onClickPdf = async (item: IDocument, docType?: string) => {
    if (!user || !REACT_APP_HOSPITAL_CODE) return;
    setOpenPdfState({ loader: true, error: null });
    await onClickDownloadPdf(item, dispatch);
    createLog(
      user,
      USER_ACTIVITY_EVENT.VIEW,
      USER_ACTIVITY_TYPE.PDF,
      docType || getDocType(item.caseType) || ''
    );
    setOpenPdfState({ loader: false, error: null });
  };

  const onClickViewAll = (route: string) => {
    navigate(`${route}`);
  };

  const getGroupIndex = (docType: MEDICAL_HISTORY) => {
    const groupArray = [
      {
        type: MEDICAL_HISTORY.RADIOLOGY,
        check: (allDocuments?.RadiologyDocList?.length ?? 0) > 0
      },
      {
        type: MEDICAL_HISTORY.LAB,
        check: (allDocuments?.LabDocList?.length ?? 0) > 0
      },
      {
        type: MEDICAL_HISTORY.RELEASE_DOCS,
        check: (allDocuments?.ReleaseDocList?.length ?? 0) > 0
      }
    ];

    const existingGroups = groupArray
      .filter((group) => group.check)
      .map((group) => group.type);

    return existingGroups.indexOf(docType);
  };

  const getTextColor = (index: number) => {
    return index === 0 ? 'white' : theme.colors.primary;
  };

  const getContent = () => {
    if (allDocumentsLoader) {
      return (
        <div className={classes.loadingPage}>
          <img
            width={150}
            src={hospital?.logo || '/assets/space_saver.png'}
            alt={t('accessibility.ministryOfHealthLogo')}
            title={t('accessibility.ministryOfHealthLogo')}
            // style={{ backgroundColor: 'white' }}
          />
          <Spin />
        </div>
      );
    }
    if (allDocumentsError) {
      notificationError({
        message: translateError(getErrorMessage(allDocumentsError))
      });
      return;
    }
    if (
      !allDocuments?.LabDocList?.length &&
      !allDocuments?.RadiologyDocList?.length &&
      !allDocuments?.ReleaseDocList?.length
    ) {
      return <NoMatch />;
    } else {
      return (
        <div className={classes.mainContainer}>
          <Flex vertical align="center" justify="center">
            <Flex className={classes.cardRow}>
              <Row className={classes.cardRow}>
                {allDocuments && allDocuments?.RadiologyDocList?.length > 0 && (
                  <>
                    <Flex
                      justify="space-between"
                      align="center"
                      style={{
                        color: getTextColor(
                          getGroupIndex(MEDICAL_HISTORY.RADIOLOGY)
                        )
                      }}
                      className={`${classes.titleContainer} ${classes.title}`}
                    >
                      <p>
                        {`${t('medicalHistory.radiology')} (${allDocuments?.RadiologyDocList?.length})`}
                      </p>
                      <p
                        className={classes.viewAllButton}
                        onClick={() => onClickViewAll(APP_ROUTES.RADIOLOGY)}
                      >
                        {`${t('medicalHistory.forAll')} ${t('medicalHistory.radiology')}>>`}
                      </p>
                    </Flex>

                    {width && width >= MEDIA_QUERIES.MOBILE && (
                      <Row className={classes.docListContainer}>
                        {getTop4DocumentsByDate(
                          allDocuments.RadiologyDocList
                        )?.map((doc, i) => (
                          <Col
                            span={
                              getColumnSpan(
                                getTop4DocumentsByDate(
                                  allDocuments?.RadiologyDocList
                                )?.length
                              ) as number
                            }
                            key={i}
                          >
                            <Card
                              downloadPdf={onClickPdf}
                              doc={doc}
                              key={i}
                              docType={MEDICAL_HISTORY.RADIOLOGY}
                            ></Card>
                          </Col>
                        ))}
                      </Row>
                    )}
                    {width && width < MEDIA_QUERIES.MOBILE && (
                      <DocumentsList
                        list={getTop4DocumentsByDate(
                          allDocuments?.RadiologyDocList
                        )}
                      />
                    )}
                  </>
                )}
              </Row>
            </Flex>
            <Flex className={classes.cardRow}>
              <Row className={classes.cardRow}>
                {allDocuments && allDocuments?.LabDocList?.length > 0 && (
                  <>
                    <Flex
                      justify="space-between"
                      align="center"
                      className={`${classes.titleContainer} ${classes.title}`}
                      style={{
                        color: getTextColor(getGroupIndex(MEDICAL_HISTORY.LAB))
                      }}
                    >
                      <p>
                        {`${t('medicalHistory.lab')} (${allDocuments?.LabDocList?.length})`}
                      </p>
                      <p
                        className={classes.viewAllButton}
                        onClick={() => onClickViewAll(APP_ROUTES.LAB)}
                      >
                        {`${t('medicalHistory.forAll')} ${t('medicalHistory.lab')}>>`}
                      </p>
                    </Flex>

                    {width && width > MEDIA_QUERIES.MOBILE && (
                      <Row className={classes.docListContainer}>
                        {getTop4DocumentsByDate(allDocuments.LabDocList)?.map(
                          (doc, i) => (
                            <Col
                              span={
                                getColumnSpan(
                                  getTop4DocumentsByDate(
                                    allDocuments?.LabDocList
                                  )?.length
                                ) as number
                              }
                              key={i}
                            >
                              <Card
                                doc={doc}
                                key={i}
                                downloadPdf={onClickPdf}
                                docType={MEDICAL_HISTORY.LAB}
                              ></Card>
                            </Col>
                          )
                        )}
                      </Row>
                    )}
                    {width && width < MEDIA_QUERIES.MOBILE && (
                      <DocumentsList
                        list={getTop4DocumentsByDate(allDocuments?.LabDocList)}
                      />
                    )}
                  </>
                )}
              </Row>
            </Flex>
          </Flex>

          {allDocuments && allDocuments?.ReleaseDocList?.length > 0 && (
            <Flex>
              <Row className={classes.docListRow}>
                {documentTypes.map((type) => {
                  const filteredList = allDocuments?.ReleaseDocList.filter(
                    (document) => document.caseType === type
                  );

                  const isLastType = type === DOCUMENT_TYPE_KEY.RELEASE_DOCS;
                  const allList = areAllListsNotEmpty();
                  const isOtherList = isOtherListNotEmpty(type);
                  let span = isLastType && allList ? 24 : isOtherList ? 12 : 24;
                  if ((width ?? 0) < MEDIA_QUERIES.MOBILE) {
                    span = 24;
                  }
                  return (
                    <Col span={span} key={type}>
                      {filteredList.length > 0 && (
                        <div>
                          <Flex
                            justify="space-between"
                            className={classes.titleContainer}
                            style={{
                              color: getTextColor(
                                getGroupIndex(MEDICAL_HISTORY.RELEASE_DOCS)
                              )
                            }}
                          >
                            <p>
                              {`${getDocTypeName(filteredList[0]?.caseType)} (${filteredList?.length})`}
                            </p>
                            <p
                              className={classes.viewAllButton}
                              onClick={() =>
                                onClickViewAll(
                                  getDocType(filteredList[0]?.caseType)
                                )
                              }
                            >
                              {`${t('medicalHistory.forAll')} 
                              ${getDocTypeName(filteredList[0]?.caseType)}
                              >>`}
                            </p>
                          </Flex>
                          <DocumentsList list={filteredList} />
                        </div>
                      )}
                    </Col>
                  );
                })}
              </Row>
            </Flex>
          )}
        </div>
      );
    }
  };

  return <>{getContent()}</>;
};

export default MedicalHistory;
